import {CountBadge, PhoneHQGlyph} from 'ch3-ui-lib';
import {List, Moon} from "lucide-react";
import {forwardRef, useState, type ComponentPropsWithoutRef, type ReactNode} from "react";
import {useNavigate} from "react-router-dom";
import {
    MsisdnSelectItem, PhoneNumberDndStatus,
    useGetPhoneNumberDndStatusQuery,
    useGetUserMsisdnsQuery,
    useTogglePhooneNumberDndMutation
} from "../../store/features/Msisdns/msisdnService.ts";
import {
    GroupListItem,
    useGroupsForFiltersQuery
} from "../../store/features/groups/groupsService.ts";
import {useAppDispatch} from "../../store/store.ts";
import {
    getSelectedGroups,
    getSelectedNumber,
    setGroupsFilter,
    setPhoneNumbersFilter,
} from "../../store/features/Filters/filtersSlice.tsx";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice.ts";
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis.ts";
import {DndDialog} from "../Dnd/DndDialog.tsx";
import {DndStatusBanner, LineContextMenu} from "../Dnd/LineDnd.tsx";
import {isDndOn as isDndActive, parseDue, toDndPayload} from "../Dnd/dndTime.ts";
import {selectMissedCallsByNumber} from "../../store/features/MissedCalls/missedCallsSlice.ts";

export interface GlobalDndProps {
    /** Global DND from the user's status; when on, every line is silenced. */
    globalDnd: { enabled: boolean; dueDate: Date | null };
    onTurnOffGlobalDnd: () => void;
}

const CallsSubmenu = ({globalDnd, onTurnOffGlobalDnd}: GlobalDndProps) => {
    const dispatch = useAppDispatch();

    const userId = useSelector(selectUserId);
    const {data: userMsisdns} = useGetUserMsisdnsQuery({userId, phrase: ''});
    const {data: groupsData} = useGroupsForFiltersQuery({userId});

    const selectedPhoneNumber = useSelector(getSelectedNumber);
    const selectedGroup = useSelector(getSelectedGroups);

    const isMyNumbersSelected = !selectedPhoneNumber && !selectedGroup;
    const missedCallsData = useSelector(selectMissedCallsByNumber);
    const { data: msisdnDndData } =  useGetPhoneNumberDndStatusQuery({userId}, {pollingInterval: 15000});
    const handlePhoneNumberSelect = (value: MsisdnSelectItem) => {
        dispatch(setPhoneNumbersFilter(value));
        dispatch(setGroupsFilter(null));
    }

    const handleUserAndGroupSelect = (value: GroupListItem) => {
        dispatch(setGroupsFilter(value));
        dispatch(setPhoneNumbersFilter(null))
    }

    const handleMyNumbersSelect = () => {
        dispatch(setPhoneNumbersFilter(null));
        dispatch(setGroupsFilter(null));
    }

    const isPhoneNumberSelected = (msisdn: MsisdnSelectItem) => {
        return selectedPhoneNumber && selectedPhoneNumber.msisdn === msisdn.msisdn;
    }

    const isGroupSelected = (group: GroupListItem) => {
        return selectedGroup && selectedGroup &&
            selectedGroup.groupId === group.groupId;
    }

    const navigate = useNavigate();
    const [togglePhoneNumberDnd] = useTogglePhooneNumberDndMutation();
    // The line whose DND dialog is open; `adjust` opens it on the options even while DND is on.
    const [dndLine, setDndLine] = useState<{ line: MsisdnSelectItem; adjust: boolean } | null>(null);

    const lineDnd = (msisdn: MsisdnSelectItem): PhoneNumberDndStatus | undefined =>
        msisdnDndData?.phoneNumbers.find((el: PhoneNumberDndStatus) => isDndActive(el) && el.phoneNumberId === msisdn.msisdnId);
    const isDndOn = (msisdn: MsisdnSelectItem) => Boolean(lineDnd(msisdn));
    const silencedLines = (userMsisdns?.data ?? []).filter(isDndOn);

    const setLineDnd = (line: MsisdnSelectItem, enabled: boolean, dueDate: Date | null) =>
        togglePhoneNumberDnd({enabled, userId, dueDate: toDndPayload(dueDate), phoneNumberId: line.msisdnId}).unwrap();

    const turnOffBanner = () => {
        if (globalDnd.enabled) return onTurnOffGlobalDnd();
        silencedLines.forEach((line) => setLineDnd(line, false, null));
    };

    return (
        <aside aria-label="Lines" className='flex h-full w-[240px] flex-none flex-col border-0 border-r border-solid border-line bg-surface px-2 text-ink'>
            <div className='flex h-header shrink-0 items-center px-2'>
                <h2 className='m-0 text-title text-ink'>Calls</h2>
            </div>

            <LineRow
                label='All lines'
                icon={<List size={16} aria-hidden="true" className="!p-0 !text-current"/>}
                selected={isMyNumbersSelected}
                onClick={handleMyNumbersSelect}
                compact
            />

            <DndStatusBanner
                global={globalDnd}
                lines={silencedLines.map((line) => ({label: line.label, dueDate: parseDue(lineDnd(line)?.dueDate)}))}
                onTurnOff={turnOffBanner}
            />

            {Boolean(userMsisdns?.data?.length) && <SectionLabel>My numbers</SectionLabel>}
            <div className='max-h-[50vh] shrink-0 overflow-y-auto overflow-x-hidden'>
                {userMsisdns?.data?.map((msisdn: MsisdnSelectItem) => (
                    <LineContextMenu
                        key={msisdn.msisdn}
                        dndOn={isDndOn(msisdn)}
                        onDnd={() => setDndLine({line: msisdn, adjust: false})}
                        onTurnOff={() => setLineDnd(msisdn, false, null)}
                        onAdjust={() => setDndLine({line: msisdn, adjust: true})}
                        onSettings={() => navigate(`/phoneNumbers/${msisdn.msisdnId}`)}
                    >
                    <LineRow
                        label={msisdn.label}
                        subline={getFormatedPhoneNumber(msisdn.msisdn, true)}
                        icon={<PhoneHQGlyph name={msisdn.icon || 'call'} size={14}/>}
                        selected={Boolean(isPhoneNumberSelected(msisdn))}
                        onClick={() => handlePhoneNumberSelect(msisdn)}
                        trailing={
                            <>
                                {Boolean(missedCallsData?.[msisdn.msisdn]?.count) && (
                                    <CountBadge count={missedCallsData[msisdn.msisdn].count} label={`${missedCallsData[msisdn.msisdn].count} missed during Do Not Disturb`}/>
                                )}
                                {isDndOn(msisdn) && <Moon size={16} aria-label="Do not disturb is on" className="!p-0 shrink-0 !text-brand-ink"/>}
                            </>
                        }
                    />
                    </LineContextMenu>
                ))}
            </div>

            {Boolean(groupsData?.length) && <SectionLabel>Team</SectionLabel>}
            <div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
                {groupsData?.map((el: GroupListItem) => (
                    <LineRow
                        key={el.groupId}
                        label={el.name}
                        icon={<PhoneHQGlyph name={el.icon || 'group'} size={14}/>}
                        selected={Boolean(isGroupSelected(el))}
                        onClick={() => handleUserAndGroupSelect(el)}
                    />
                ))}
            </div>

            {dndLine && (
                <DndDialog
                    open
                    onOpenChange={(open) => !open && setDndLine(null)}
                    scope={{kind: 'line', label: dndLine.line.label, number: getFormatedPhoneNumber(dndLine.line.msisdn, true)}}
                    enabled={isDndOn(dndLine.line)}
                    dueDate={parseDue(lineDnd(dndLine.line)?.dueDate)}
                    adjust={dndLine.adjust}
                    onTurnOn={(dueDate) => setLineDnd(dndLine.line, true, dueDate)}
                    onTurnOff={() => setLineDnd(dndLine.line, false, null)}
                />
            )}
        </aside>
    );
}

const SectionLabel = ({children}: { children: ReactNode }) => (
    <div className='px-2 pb-1 pt-3 text-caption text-ink-muted'>{children}</div>
);

interface LineRowProps {
    label: string;
    subline?: string;
    icon: ReactNode;
    selected: boolean;
    onClick: () => void;
    trailing?: ReactNode;
    compact?: boolean;
}

// A line or team in the Calls column: 28px tile, name and number, brand tint when selected.
// `box-border`: ch3-ui-lib's global `button { all: unset }` resets box-sizing to content-box,
// so w-full + px-2 overflowed the column by 16px (the highlight ran past the border).
// forwardRef + rest props: the row is the trigger of the line context menu (Radix `asChild`).
const LineRow = forwardRef<HTMLButtonElement, LineRowProps & Omit<ComponentPropsWithoutRef<'button'>, keyof LineRowProps>>(
    ({label, subline, icon, selected, onClick, trailing, compact, ...rest}, ref) => (
    <button
        {...rest}
        ref={ref}
        type='button'
        onClick={onClick}
        aria-current={selected ? 'true' : undefined}
        className={`box-border flex w-full cursor-pointer items-center gap-2.5 rounded-control px-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${compact ? 'h-10' : 'h-12'} ${
            selected ? 'bg-brand-tint-subtle' : 'hover:bg-surface-muted'
        }`}
    >
        <span className={`flex size-7 shrink-0 items-center justify-center rounded-chip ${selected ? 'bg-brand-tint text-brand-ink' : 'bg-surface-muted text-ink-secondary'}`}>
            {icon}
        </span>
        <span className='flex min-w-0 flex-1 flex-col'>
            <span className={`truncate text-callout ${selected ? 'font-semibold' : 'font-medium'}`}>{label}</span>
            {subline && <span className='truncate text-footnote text-ink-muted'>{subline}</span>}
        </span>
        {trailing}
    </button>
));
LineRow.displayName = 'LineRow';

const EmptySubmenu = () => {
    return null;
}

export const SidebarSubmenu = (props: GlobalDndProps) => {
    const path = window.location.pathname;
    const showCallsSubmenu = path === '/calls';

    return (
        <>
            {showCallsSubmenu ? <CallsSubmenu {...props}/> : <EmptySubmenu />}
        </>
    );
}